import { api } from "./client";

export interface AdminOverview {
  users: {
    total: number;
    admins: number;
    new_last_30d: number;
    active_7d: number;
  };
  waitlist: {
    total: number;
    confirmed: number;
    new_last_7d: number;
    converted: number;
    convert_pct: number;
  };
  ai_spend: {
    this_month_usd: number;
    prev_month_usd: number;
    calls_today: number;
  };
  generated_at: string;
}

export interface AdminUserRow {
  id: number;
  email: string;
  language: "en" | "ru" | "uk";
  is_admin: boolean;
  created_at: string | null;
  spend_this_month_usd: number;
  calls_this_month: number;
  spend_total_usd: number;
}

export interface AdminUsersPage {
  total: number;
  limit: number;
  offset: number;
  users: AdminUserRow[];
}

export interface AdminUserDetail {
  user: {
    id: number;
    email: string;
    language: string;
    is_admin: boolean;
    created_at: string | null;
  };
  activity: {
    workouts: number;
    meals: number;
    chat_messages: number;
  };
  spend: {
    by_model: Array<{
      model: string;
      usd: number;
      calls: number;
      input_tokens: number;
      output_tokens: number;
    }>;
    by_operation: Array<{ operation: string; usd: number; calls: number }>;
    by_month: Array<{ month: string; usd: number }>;
  };
}

export interface AdminAiSpend {
  period_days: number;
  total_period_usd: number;
  failed_calls_period: number;
  daily_timeseries: Array<{
    date: string;
    model: string;
    usd: number;
    calls: number;
  }>;
  top_users_this_month: Array<{
    user_id: number;
    email: string;
    usd: number;
    calls: number;
  }>;
  by_operation_model: Array<{
    operation: string;
    model: string;
    usd: number;
    calls: number;
  }>;
}

export interface AdminWaitlistEntry {
  id: number;
  position: number;
  email: string;
  name: string | null;
  lang: string | null;
  goal_race: string | null;
  race_date: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer: string | null;
  confirmed: boolean;
  invited: boolean;
  invited_at: string | null;
  converted_user_id: number | null;
  is_founding: boolean;
  removed_at: string | null;
  created_at: string | null;
}

export interface AdminWaitlistPage {
  total: number;
  filtered_total: number;
  by_source: Record<string, number>;
  entries: AdminWaitlistEntry[];
}

export interface AdminWaitlistMetrics {
  total: number;
  founding_total: number;
  founding_claimed: number;
  founding_remaining: number;
  last_24h: number;
  last_7d: number;
  converted: number;
  removed: number;
  by_lang: Record<string, number>;
}

export interface AdminSystemHealth {
  scheduler: {
    running: boolean;
    jobs: Array<{
      id: string;
      name: string | null;
      next_run: string | null;
      trigger: string;
    }>;
    error?: string;
  };
  recent_llm_errors: Array<{
    id: number;
    user_id: number | null;
    model: string;
    operation: string;
    error: string;
    created_at: string | null;
  }>;
  db_size_mb: number;
}

export interface AdminPayments {
  status: string;
  message: string;
  mrr_usd: number;
  arr_usd: number;
  transactions: unknown[];
}

export async function getAdminOverview(): Promise<AdminOverview> {
  const { data } = await api.get<AdminOverview>("/admin/overview");
  return data;
}

export async function getAdminUsers(params?: {
  q?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminUsersPage> {
  const { data } = await api.get<AdminUsersPage>("/admin/users", { params });
  return data;
}

export async function getAdminUserDetail(id: number): Promise<AdminUserDetail> {
  const { data } = await api.get<AdminUserDetail>(`/admin/users/${id}`);
  return data;
}

export async function getAdminAiSpend(days = 30): Promise<AdminAiSpend> {
  const { data } = await api.get<AdminAiSpend>("/admin/ai-spend", {
    params: { days },
  });
  return data;
}

export async function getAdminWaitlist(params?: {
  converted?: boolean;
  invited?: boolean;
  source?: string;
  q?: string;
  lang?: string;
  founding?: boolean;
  include_removed?: boolean;
  sort?: "joined_desc" | "joined_asc" | "position_asc" | "position_desc";
  limit?: number;
  offset?: number;
}): Promise<AdminWaitlistPage> {
  const { data } = await api.get<AdminWaitlistPage>("/admin/waitlist", { params });
  return data;
}

export async function getAdminWaitlistMetrics(): Promise<AdminWaitlistMetrics> {
  const { data } = await api.get<AdminWaitlistMetrics>("/admin/waitlist/metrics");
  return data;
}

export async function adminWaitlistMarkConverted(
  id: number,
  user_id?: number,
): Promise<{ id: number; converted_user_id: number | null }> {
  const { data } = await api.post(
    `/admin/waitlist/${id}/convert`,
    null,
    { params: user_id != null ? { user_id } : undefined },
  );
  return data;
}

export async function adminWaitlistSoftDelete(
  id: number,
): Promise<{ id: number; removed_at: string }> {
  const { data } = await api.delete(`/admin/waitlist/${id}`);
  return data;
}

export function adminWaitlistCsvExportUrl(includeRemoved = false): string {
  const qs = includeRemoved ? "?include_removed=true" : "";
  return `/api/admin/waitlist/export.csv${qs}`;
}

export async function adminInviteWaitlistEntry(
  id: number,
): Promise<{ id: number; email: string; invited: boolean }> {
  const { data } = await api.post(`/admin/waitlist/${id}/invite`);
  return data;
}

export async function getAdminSystemHealth(): Promise<AdminSystemHealth> {
  const { data } = await api.get<AdminSystemHealth>("/admin/system/health");
  return data;
}

export async function getAdminPayments(): Promise<AdminPayments> {
  const { data } = await api.get<AdminPayments>("/admin/payments");
  return data;
}

// ── Phase29: cost dashboard ───────────────────────────────────────────────

export interface AdminCostsToday {
  date: string;
  total_usd: number;
  calls: number;
  searches: number;
  search_usd: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  input_tokens: number;
  cache_hit_rate: number;
  by_model: Array<{ model: string; usd: number; calls: number }>;
}

export interface AdminCostsTimeseriesRow {
  date: string;
  usd: number;
  calls: number;
  searches: number;
  cache_read_tokens: number;
}

export interface AdminCostsCacheRow {
  id: number;
  query: string;
  topic: string | null;
  hits: number;
  result_count: number;
  status: string;
  fresh: boolean;
  last_verified_at: string | null;
}

export interface AdminCosts {
  today: AdminCostsToday;
  period_days: number;
  period_timeseries: AdminCostsTimeseriesRow[];
  search_cache: {
    rows: number;
    total_hits: number;
    estimated_savings_usd: number;
    top: AdminCostsCacheRow[];
  };
}

export async function getAdminCosts(days = 7): Promise<AdminCosts> {
  const { data } = await api.get<AdminCosts>("/admin/costs", { params: { days } });
  return data;
}
